/**
 * /admin/vault — Plant-linked Compliance Vault + evidence-based
 * Corridor Verified ceremony.
 *
 * Every document links to ONE exact plant (slug + P## + legal entity).
 * Corridor Verified can ONLY be granted through the review ceremony below
 * (reviewer identity, exact plant confirmation, notes, applicability
 * checks, decision + reason). Raw files stay private; public pages get
 * safe metadata only.
 */
import React, { useEffect, useMemo, useState } from "react";
import {
  ShieldCheck, Upload, FileText, X, AlertOctagon, RotateCcw, History,
  KeyRound, Loader2, Search,
} from "lucide-react";
import api from "../api";
import { useI18n } from "../i18n";
import {
  CorridorVerifiedBadge, GovListedBadge, HelpTip,
} from "./adminUiHelpers";
import { ClaimSubmissionsQueue } from "../components/ClaimSubmissionsQueue";

const STATUS_STYLES = {
  evidence_requested:  { bg: "#f1f5f9", color: "#334155", label: "Evidence Requested" },
  under_review:        { bg: "#dbeafe", color: "#1e40af", label: "Under Review" },
  current:             { bg: "#dcfce7", color: "#166534", label: "Current" },
  expiring:            { bg: "#fef3c7", color: "#92400e", label: "Expiring" },
  expired:             { bg: "#fee2e2", color: "#991b1b", label: "Expired" },
  rejected:            { bg: "#fee2e2", color: "#991b1b", label: "Rejected" },
  revoked:             { bg: "#fecaca", color: "#7f1d1d", label: "Revoked" },
  scope_not_confirmed: { bg: "#ffedd5", color: "#9a3412", label: "Scope Not Confirmed" },
};

export function VaultStatusChip({ status, testid }) {
  const s = STATUS_STYLES[status] || { bg: "#f8fafc", color: "#64748b", label: status };
  return (
    <span
      className="font-mono"
      style={{
        fontSize: 10, padding: "3px 7px", background: s.bg, color: s.color,
        borderRadius: 2, whiteSpace: "nowrap", letterSpacing: "0.03em",
      }}
      data-testid={testid}
    >
      {s.label}
    </span>
  );
}

const DOC_TYPES = [
  "IMMEX Program Authorization", "ISO 9001", "IATF 16949", "ISO 13485",
  "AS9100", "C-TPAT / OEA", "Site Evidence (photo / utility bill)", "Other",
];
const CHECK_LABELS = {
  issuer_verified: "Issuer verified as a real certification body / authority",
  certificate_number_matches: "Certificate number matches the document",
  scope_covers_plant: "Scope explicitly covers THIS plant location",
  dates_valid: "Issue / expiration dates are valid and legible",
  document_authentic: "Document appears authentic (no alteration)",
};

export default function AdminVault() {
  const { lang } = useI18n();
  const [plants, setPlants] = useState([]);
  const [plantQuery, setPlantQuery] = useState("");
  const [selected, setSelected] = useState(null);
  const [docs, setDocs] = useState([]);
  const [docsLoading, setDocsLoading] = useState(true);
  const [requests, setRequests] = useState([]);
  const [audit, setAudit] = useState([]);
  const [reviewDoc, setReviewDoc] = useState(null);
  const [replaceDoc, setReplaceDoc] = useState(null);
  const [historyDoc, setHistoryDoc] = useState(null);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/manufacturers", { params: { limit: 2000 } });
      const items = Array.isArray(data) ? data : [];
      setPlants(items);
      const pre = new URLSearchParams(window.location.search).get("plant");
      if (pre) {
        const m = items.find((p) => (p.slug || "").toLowerCase() === pre.toLowerCase());
        if (m) { setSelected(m); setPlantQuery(""); }
      }
    })();
  }, []);

  async function loadDocs() {
    setDocsLoading(true);
    try {
      const params = selected ? { plant_slug: selected.slug } : {};
      params.include_superseded = true; // superseded versions stay visible with a chip
      const { data } = await api.get("/admin/vault/documents", { params });
      setDocs(data.items || []);
    } finally { setDocsLoading(false); }
  }
  async function loadRequests() {
    const { data } = await api.get("/admin/vault/access-requests");
    setRequests(data.items || []);
  }
  async function loadAudit() {
    const { data } = await api.get("/admin/vault/audit", { params: { limit: 60 } });
    setAudit(data.items || []);
  }
  function reloadAll() { loadDocs(); loadRequests(); loadAudit(); }
  useEffect(() => { loadDocs(); /* eslint-disable-next-line */ }, [selected]);
  useEffect(() => { loadRequests(); loadAudit(); }, []);

  const plantMatches = useMemo(() => {
    const q = plantQuery.trim().toLowerCase();
    if (!q) return [];
    return plants
      .filter((p) => [p.display_name, p.company_name, p.legal_entity, p.slug,
        p.plant_location_id, p.plant_sequence_label]
        .some((v) => (v || "").toString().toLowerCase().includes(q)))
      .slice(0, 12);
  }, [plants, plantQuery]);

  return (
    <div className="container-cd py-10 pb-24" data-testid="admin-vault-page">
      <div className="overline">ADMIN · COMPLIANCE VAULT</div>
      <h1 className="font-display text-4xl md:text-5xl mt-3 leading-[0.95]" data-testid="vault-title">
        Compliance Vault &amp; Verification Ceremony
      </h1>
      <p className="mt-4 text-sm max-w-3xl" style={{ color: "var(--ink-2)" }}>
        Evidence documents link to ONE exact plant location (P##). <strong>Corridor Verified
        is never automatic</strong> — it is granted only when a reviewer approves evidence for
        that exact plant through the ceremony below. Government-listed IMMEX status stays
        separate. Raw files remain private; public pages show safe metadata only.
      </p>

      {msg ? (
        <div className="mt-4 cd-card p-3 font-mono text-xs" style={{ borderLeft: "3px solid var(--green)" }} data-testid="vault-msg">
          {msg}
        </div>
      ) : null}

      {/* ── Plant selection ─────────────────────────────────────────── */}
      <section className="mt-8 cd-card p-5" data-testid="vault-plant-picker">
        <div className="overline flex items-center">
          <span>1 · SELECT THE EXACT PLANT</span>
          <HelpTip text="Evidence is linked to one plant location, not a company. Pick the exact P## record the document belongs to." />
        </div>
        <div className="mt-3 flex items-center gap-2 bg-white" style={{ border: "1px solid var(--ink)", padding: "0 10px", maxWidth: 560 }}>
          <Search size={15} strokeWidth={1.6} />
          <input
            value={plantQuery}
            onChange={(e) => setPlantQuery(e.target.value)}
            placeholder="Search plant by name, slug, P##, IMMEX location id…"
            className="flex-1 outline-none font-body"
            style={{ fontSize: 15, minHeight: 40, background: "transparent" }}
            data-testid="vault-plant-search"
          />
        </div>
        {plantMatches.length > 0 ? (
          <div className="mt-2 space-y-1" data-testid="vault-plant-results">
            {plantMatches.map((p) => (
              <button
                key={p.slug}
                type="button"
                onClick={() => { setSelected(p); setPlantQuery(""); }}
                className="w-full text-left font-mono text-xs flex flex-wrap items-center gap-3"
                style={{ border: "1px solid var(--line)", padding: "8px 10px", background: "white", cursor: "pointer" }}
                data-testid={`vault-plant-option-${p.slug}`}
              >
                <strong>{p.display_name || p.company_name || p.slug}</strong>
                {p.plant_sequence_label ? <span style={{ background: "#f1f5f9", padding: "1px 5px" }}>{p.plant_sequence_label}</span> : null}
                <span style={{ color: "var(--ink-2)" }}>{p.slug}</span>
                {p.plant_location_id ? <span style={{ color: "var(--ink-2)" }}>{p.plant_location_id}</span> : null}
              </button>
            ))}
          </div>
        ) : null}
        {selected ? (
          <div className="mt-4 cd-card p-4" style={{ background: "#fdfdfd" }} data-testid="vault-selected-plant">
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex-1 min-w-0">
                <div className="font-heading font-semibold" data-testid="vault-selected-name">
                  {selected.display_name || selected.company_name || selected.slug}
                </div>
                <div className="font-mono text-xs mt-1" style={{ color: "var(--ink-2)" }}>
                  {selected.plant_sequence_label ? `${selected.plant_sequence_label} · ` : ""}
                  {selected.slug}
                  {selected.plant_location_id ? ` · ${selected.plant_location_id}` : ""}
                  {selected.legal_entity ? ` · ${selected.legal_entity}` : ""}
                </div>
              </div>
              {selected.corridor_verified ? <CorridorVerifiedBadge /> : ((selected.slug || "").startsWith("immex-") ? <GovListedBadge /> : null)}
              <button type="button" onClick={() => setSelected(null)} className="btn btn-ghost" style={{ padding: 6, minHeight: 0 }} data-testid="vault-clear-plant">
                <X size={14} />
              </button>
            </div>
          </div>
        ) : null}
      </section>

      {/* ── Upload ─────────────────────────────────────────────────── */}
      {selected ? (
        <UploadForm plant={selected} onDone={(m) => { setMsg(m); reloadAll(); }} />
      ) : null}

      {/* ── Documents ──────────────────────────────────────────────── */}
      <section className="mt-8" data-testid="vault-docs-section">
        <div className="overline mb-2">
          3 · EVIDENCE DOCUMENTS {selected ? `· ${selected.slug}` : "· ALL PLANTS"} ({docs.length})
        </div>
        {docsLoading ? (
          <div className="font-mono text-xs overline">Loading…</div>
        ) : docs.length === 0 ? (
          <div className="cd-card p-6 font-mono text-xs" style={{ color: "var(--ink-2)" }} data-testid="vault-docs-empty">
            No evidence documents yet{selected ? " for this plant" : ""}.
          </div>
        ) : (
          <div className="space-y-2">
            {docs.map((d) => (
              <DocRow
                key={d.doc_id}
                d={d}
                onReview={() => setReviewDoc(d)}
                onReplace={() => setReplaceDoc(d)}
                onHistory={() => setHistoryDoc(d)}
                onRevoked={(m) => { setMsg(m); reloadAll(); }}
              />
            ))}
          </div>
        )}
      </section>

      {/* ── Supplier submissions (Claim or Confirm) ─────────────────── */}
      <ClaimSubmissionsQueue />

      {/* ── Access requests ────────────────────────────────────────── */}
      <AccessRequestsPanel requests={requests} onChanged={(m) => { setMsg(m); loadRequests(); loadAudit(); }} />

      {/* ── Audit log ──────────────────────────────────────────────── */}
      <section className="mt-10 cd-card p-5" data-testid="vault-audit-panel">
        <div className="overline flex items-center">
          <span>AUDIT LOG · APPEND-ONLY</span>
          <HelpTip text="Every upload, review, approval, rejection, replacement, access, download, revocation, and expiry is recorded here." />
        </div>
        <div className="mt-3 overflow-x-auto">
          <table className="w-full font-mono" style={{ fontSize: 11 }}>
            <thead>
              <tr style={{ color: "var(--ink-2)", textAlign: "left" }}>
                <th style={{ padding: "4px 8px" }}>When</th>
                <th style={{ padding: "4px 8px" }}>Event</th>
                <th style={{ padding: "4px 8px" }}>Doc</th>
                <th style={{ padding: "4px 8px" }}>Plant</th>
                <th style={{ padding: "4px 8px" }}>Actor</th>
                <th style={{ padding: "4px 8px" }}>Details</th>
              </tr>
            </thead>
            <tbody data-testid="vault-audit-rows">
              {audit.map((e) => (
                <tr key={e.event_id} style={{ borderTop: "1px solid var(--line)" }} data-testid={`audit-row-${e.event_id}`}>
                  <td style={{ padding: "5px 8px", whiteSpace: "nowrap", color: "var(--ink-2)" }}>{(e.at || "").replace("T", " ").slice(0, 19)}</td>
                  <td style={{ padding: "5px 8px" }}><strong>{e.event}</strong></td>
                  <td style={{ padding: "5px 8px", color: "var(--ink-2)" }}>{e.doc_id || "—"}</td>
                  <td style={{ padding: "5px 8px", color: "var(--ink-2)" }}>{e.plant_slug || "—"}</td>
                  <td style={{ padding: "5px 8px" }}>{e.actor || "—"}</td>
                  <td style={{ padding: "5px 8px", color: "var(--ink-2)", maxWidth: 340 }} className="truncate">
                    {JSON.stringify(e.details || {})}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {reviewDoc ? (
        <ReviewCeremonyModal
          doc={reviewDoc}
          onClose={() => setReviewDoc(null)}
          onDone={(m) => { setReviewDoc(null); setMsg(m); reloadAll(); }}
        />
      ) : null}
      {replaceDoc ? (
        <ReplaceModal
          doc={replaceDoc}
          onClose={() => setReplaceDoc(null)}
          onDone={(m) => { setReplaceDoc(null); setMsg(m); reloadAll(); }}
        />
      ) : null}
      {historyDoc ? (
        <HistoryModal doc={historyDoc} onClose={() => setHistoryDoc(null)} />
      ) : null}
    </div>
  );
}

/* ── Upload form ─────────────────────────────────────────────────── */
function UploadForm({ plant, onDone }) {
  const [f, setF] = useState({
    document_type: DOC_TYPES[0], issuer: "", certificate_number: "",
    scope: "", issue_date: "", expiration_date: "",
  });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  function up(k, v) { setF((p) => ({ ...p, [k]: v })); }

  async function submit(withFile) {
    setBusy(true); setErr("");
    try {
      const fd = new FormData();
      fd.append("plant_slug", plant.slug);
      Object.entries(f).forEach(([k, v]) => fd.append(k, v));
      if (withFile && file) fd.append("file", file);
      await api.post("/admin/vault/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onDone(withFile
        ? `Evidence uploaded for ${plant.slug} — status: Under Review.`
        : `Evidence requested for ${plant.slug} — status: Evidence Requested.`);
      setF({ document_type: DOC_TYPES[0], issuer: "", certificate_number: "", scope: "", issue_date: "", expiration_date: "" });
      setFile(null);
    } catch (e) {
      setErr(String(e.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  }

  return (
    <section className="mt-8 cd-card p-5" data-testid="vault-upload-form">
      <div className="overline flex items-center">
        <Upload size={12} className="mr-2" />
        <span>2 · ATTACH EVIDENCE TO {plant.plant_sequence_label || ""} {plant.slug}</span>
      </div>
      <div className="mt-4 grid md:grid-cols-3 gap-3">
        <div>
          <label className="overline">Document type *</label>
          <select value={f.document_type} onChange={(e) => up("document_type", e.target.value)} className="cd-input mt-2 font-mono text-xs" data-testid="vault-doc-type">
            {DOC_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <Field label="Issuer" v={f.issuer} set={(v) => up("issuer", v)} testid="vault-doc-issuer" />
        <Field label="Certificate number" v={f.certificate_number} set={(v) => up("certificate_number", v)} testid="vault-doc-cert" />
        <Field label="Scope (what exactly does it cover?)" v={f.scope} set={(v) => up("scope", v)} testid="vault-doc-scope" wide />
        <Field label="Issue date" v={f.issue_date} set={(v) => up("issue_date", v)} type="date" testid="vault-doc-issue-date" />
        <Field label="Expiration date" v={f.expiration_date} set={(v) => up("expiration_date", v)} type="date" testid="vault-doc-exp-date" />
        <div>
          <label className="overline">File (private — 10MB max)</label>
          <input type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} className="cd-input mt-2 font-mono text-xs" data-testid="vault-doc-file" />
        </div>
      </div>
      {err ? <div className="mt-3 text-sm" style={{ color: "#dc2626" }} data-testid="vault-upload-error">{err}</div> : null}
      <div className="mt-4 flex flex-wrap gap-2">
        <button type="button" onClick={() => submit(true)} disabled={busy || !file} className="btn btn-primary" data-testid="vault-upload-submit">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} Upload evidence
        </button>
        <button type="button" onClick={() => submit(false)} disabled={busy} className="btn btn-ghost" title="Creates an 'Evidence Requested' placeholder without a file." data-testid="vault-request-evidence">
          Request evidence (no file yet)
        </button>
      </div>
    </section>
  );
}

function Field({ label, v, set, type = "text", testid, wide }) {
  return (
    <div className={wide ? "md:col-span-3" : ""}>
      <label className="overline">{label}</label>
      <input type={type} value={v} onChange={(e) => set(e.target.value)} className="cd-input mt-2 font-mono text-xs" data-testid={testid} />
    </div>
  );
}

/* ── Document row ────────────────────────────────────────────────── */
function DocRow({ d, onReview, onReplace, onHistory, onRevoked }) {
  const [revoking, setRevoking] = useState(false);
  const [revokeReason, setRevokeReason] = useState("");
  const eff = d.effective_status;
  const canReview = !d.superseded && eff !== "revoked";

  async function doRevoke() {
    if (!revokeReason.trim()) return;
    try {
      await api.post(`/admin/vault/documents/${d.doc_id}/revoke`, { reason: revokeReason.trim() });
      onRevoked(`Evidence ${d.doc_id} revoked.`);
    } catch (e) {
      onRevoked(`Revoke failed: ${e.response?.data?.detail || e.message}`);
    }
  }

  return (
    <div className="cd-card p-4" data-testid={`vault-doc-${d.doc_id}`}>
      <div className="flex flex-wrap items-start gap-3">
        <FileText size={16} strokeWidth={1.6} style={{ marginTop: 3, color: "var(--ink-2)" }} />
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <strong className="text-sm">{d.document_type}</strong>
            <VaultStatusChip status={eff} testid={`vault-doc-status-${d.doc_id}`} />
            <span className="font-mono text-xs" style={{ color: "var(--ink-2)" }}>v{d.version}</span>
            {d.superseded ? (
              <span
                className="font-mono"
                style={{ fontSize: 10, padding: "3px 7px", background: "#f1f5f9", color: "#9a3412", borderRadius: 2 }}
                data-testid={`vault-doc-superseded-${d.doc_id}`}
              >
                Superseded{d.superseded_by_doc_id ? ` by ${d.superseded_by_doc_id}` : ""}
              </span>
            ) : null}
          </div>
          <div className="font-mono text-xs mt-1" style={{ color: "var(--ink-2)" }} data-testid={`vault-doc-plantlink-${d.doc_id}`}>
            {d.plant_sequence_label ? `${d.plant_sequence_label} · ` : ""}{d.plant_slug}
            {d.plant_location_id ? ` · ${d.plant_location_id}` : ""} · {d.legal_entity || d.plant_display_name}
          </div>
          <div className="font-mono text-xs mt-1" style={{ color: "var(--ink)" }}>
            {d.issuer ? `Issuer: ${d.issuer} · ` : ""}
            {d.certificate_number ? `Cert#: ${d.certificate_number} · ` : ""}
            {d.issue_date ? `Issued: ${d.issue_date} · ` : ""}
            {d.expiration_date ? `Expires: ${d.expiration_date}` : "No expiration set"}
          </div>
          {d.scope ? (
            <div className="font-mono text-xs mt-1" style={{ color: "var(--ink-2)" }} data-testid={`vault-doc-scope-${d.doc_id}`}>
              Scope: {d.scope}
            </div>
          ) : null}
          {d.review ? (
            <div className="font-mono text-xs mt-1" style={{ color: "var(--ink-2)" }} data-testid={`vault-doc-review-${d.doc_id}`}>
              Reviewed by {d.review.reviewer_email} on {d.review.review_date} → {d.review.decision} ({d.review.reason})
            </div>
          ) : null}
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          {canReview ? (
            <button type="button" onClick={onReview} className="btn btn-primary" style={{ padding: "5px 10px", fontSize: 11, minHeight: 0 }} data-testid={`vault-review-btn-${d.doc_id}`}>
              <ShieldCheck size={12} /> Review ceremony
            </button>
          ) : null}
          {!d.superseded ? (
            <button type="button" onClick={onReplace} className="btn btn-ghost" style={{ padding: "5px 10px", fontSize: 11, minHeight: 0 }} data-testid={`vault-replace-btn-${d.doc_id}`}>
              <RotateCcw size={12} /> Replace
            </button>
          ) : null}
          <button type="button" onClick={onHistory} className="btn btn-ghost" style={{ padding: "5px 10px", fontSize: 11, minHeight: 0 }} data-testid={`vault-history-btn-${d.doc_id}`}>
            <History size={12} /> History
          </button>
          {eff !== "revoked" ? (
            <button type="button" onClick={() => setRevoking((v) => !v)} className="btn" style={{ padding: "5px 10px", fontSize: 11, minHeight: 0, border: "1px solid #fca5a5", color: "#991b1b" }} data-testid={`vault-revoke-btn-${d.doc_id}`}>
              Revoke
            </button>
          ) : null}
        </div>
      </div>
      {revoking ? (
        <div className="mt-3 flex flex-wrap items-center gap-2" data-testid={`vault-revoke-form-${d.doc_id}`}>
          <input
            value={revokeReason}
            onChange={(e) => setRevokeReason(e.target.value)}
            placeholder="Revocation reason (required)"
            className="cd-input font-mono text-xs flex-1"
            style={{ maxWidth: 420, height: 34 }}
            data-testid={`vault-revoke-reason-${d.doc_id}`}
          />
          <button type="button" onClick={doRevoke} disabled={!revokeReason.trim()} className="btn" style={{ padding: "6px 12px", fontSize: 11, minHeight: 0, background: "#991b1b", color: "white" }} data-testid={`vault-revoke-confirm-${d.doc_id}`}>
            Confirm revoke
          </button>
        </div>
      ) : null}
    </div>
  );
}

/* ── Review ceremony modal ───────────────────────────────────────── */
function ReviewCeremonyModal({ doc, onClose, onDone }) {
  const [confirmSlug, setConfirmSlug] = useState("");
  const [confirmPlid, setConfirmPlid] = useState("");
  const [reviewDate, setReviewDate] = useState(new Date().toISOString().slice(0, 10));
  const [notes, setNotes] = useState("");
  const [checks, setChecks] = useState(Object.fromEntries(Object.keys(CHECK_LABELS).map((k) => [k, false])));
  const [decision, setDecision] = useState("approve");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  async function submit() {
    setBusy(true); setErr("");
    try {
      await api.post(`/admin/vault/documents/${doc.doc_id}/review`, {
        confirm_plant_slug: confirmSlug.trim(),
        confirm_plant_location_id: confirmPlid.trim(),
        review_date: reviewDate,
        notes, checks, decision, reason,
      });
      onDone(decision === "approve"
        ? `APPROVED — ${doc.plant_slug} is now Corridor Verified (evidence ${doc.doc_id}).`
        : `Review recorded: ${decision} for ${doc.doc_id}.`);
    } catch (e) {
      setErr(String(e.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  }

  return (
    <Modal onClose={onClose} testid="vault-review-modal" title="Evidence Review Ceremony">
      <div className="font-mono text-xs cd-card p-3" style={{ background: "#fdfdfd" }}>
        <strong>{doc.document_type}</strong> v{doc.version} · linked to{" "}
        <strong>{doc.plant_sequence_label ? `${doc.plant_sequence_label} · ` : ""}{doc.plant_slug}</strong>
        {doc.plant_location_id ? ` · ${doc.plant_location_id}` : ""} · {doc.legal_entity}
        <div style={{ color: "var(--ink-2)", marginTop: 4 }}>
          {doc.issuer ? `Issuer ${doc.issuer} · ` : ""}{doc.certificate_number ? `Cert# ${doc.certificate_number} · ` : ""}
          {doc.expiration_date ? `Expires ${doc.expiration_date}` : "No expiration"}
        </div>
      </div>

      <div className="mt-4 grid md:grid-cols-2 gap-3">
        <div>
          <label className="overline">Confirm exact plant slug *</label>
          <input value={confirmSlug} onChange={(e) => setConfirmSlug(e.target.value)} placeholder={`type: ${doc.plant_slug}`} className="cd-input mt-2 font-mono text-xs" data-testid="ceremony-confirm-slug" />
        </div>
        <div>
          <label className="overline">Confirm plant location id {doc.plant_location_id ? "*" : "(none on record)"}</label>
          <input value={confirmPlid} onChange={(e) => setConfirmPlid(e.target.value)} placeholder={doc.plant_location_id || "—"} className="cd-input mt-2 font-mono text-xs" data-testid="ceremony-confirm-plid" />
        </div>
        <div>
          <label className="overline">Review date *</label>
          <input type="date" value={reviewDate} onChange={(e) => setReviewDate(e.target.value)} className="cd-input mt-2 font-mono text-xs" data-testid="ceremony-review-date" />
        </div>
      </div>

      <div className="mt-4">
        <label className="overline">Evidence notes * (what did you check?)</label>
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} className="cd-input mt-2 font-mono text-xs w-full" data-testid="ceremony-notes" />
      </div>

      <div className="mt-4">
        <div className="overline">Applicability checks (all required to approve)</div>
        <div className="mt-2 space-y-1.5">
          {Object.entries(CHECK_LABELS).map(([k, label]) => (
            <label key={k} className="flex items-start gap-2 cursor-pointer font-mono text-xs">
              <input
                type="checkbox"
                checked={checks[k]}
                onChange={(e) => setChecks((p) => ({ ...p, [k]: e.target.checked }))}
                data-testid={`ceremony-check-${k}`}
              />
              <span>{label}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="mt-4 flex flex-wrap gap-4">
        {[
          ["approve", "Approve — grants Corridor Verified to this exact plant"],
          ["reject", "Reject"],
          ["scope_not_confirmed", "Scope Not Confirmed"],
        ].map(([k, label]) => (
          <label key={k} className="flex items-center gap-2 cursor-pointer font-mono text-xs">
            <input type="radio" name="ceremony-decision" checked={decision === k} onChange={() => setDecision(k)} data-testid={`ceremony-decision-${k}`} />
            {label}
          </label>
        ))}
      </div>

      <div className="mt-4">
        <label className="overline">Approval / rejection reason *</label>
        <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} className="cd-input mt-2 font-mono text-xs w-full" data-testid="ceremony-reason" />
      </div>

      {err ? <div className="mt-3 text-sm" style={{ color: "#dc2626" }} data-testid="ceremony-error">{err}</div> : null}

      <div className="mt-5 flex gap-2">
        <button type="button" onClick={submit} disabled={busy} className="btn btn-primary" data-testid="ceremony-submit">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <ShieldCheck size={14} />} Record decision
        </button>
        <button type="button" onClick={onClose} className="btn btn-ghost">Cancel</button>
      </div>
    </Modal>
  );
}

/* ── Replace modal ───────────────────────────────────────────────── */
function ReplaceModal({ doc, onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [expiration, setExpiration] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  async function submit() {
    if (!file) return;
    setBusy(true); setErr("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (expiration) fd.append("expiration_date", expiration);
      await api.post(`/admin/vault/documents/${doc.doc_id}/replace`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      onDone(`Replaced ${doc.doc_id} — new version created, pending review.`);
    } catch (e) {
      setErr(String(e.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  }
  return (
    <Modal onClose={onClose} testid="vault-replace-modal" title={`Replace evidence (creates v${(doc.version || 1) + 1})`}>
      <p className="font-mono text-xs" style={{ color: "var(--ink-2)" }}>
        The old version stays in history as superseded. The new version starts as Under Review —
        it must pass the ceremony again before it counts.
      </p>
      <div className="mt-3">
        <label className="overline">New file *</label>
        <input type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} className="cd-input mt-2 font-mono text-xs" data-testid="replace-file" />
      </div>
      <div className="mt-3">
        <label className="overline">New expiration date (optional)</label>
        <input type="date" value={expiration} onChange={(e) => setExpiration(e.target.value)} className="cd-input mt-2 font-mono text-xs" data-testid="replace-expiration" />
      </div>
      {err ? <div className="mt-3 text-sm" style={{ color: "#dc2626" }}>{err}</div> : null}
      <div className="mt-5 flex gap-2">
        <button type="button" onClick={submit} disabled={busy || !file} className="btn btn-primary" data-testid="replace-submit">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <RotateCcw size={14} />} Replace
        </button>
        <button type="button" onClick={onClose} className="btn btn-ghost">Cancel</button>
      </div>
    </Modal>
  );
}

/* ── History modal ───────────────────────────────────────────────── */
function HistoryModal({ doc, onClose }) {
  const [detail, setDetail] = useState(null);
  useEffect(() => {
    (async () => {
      const { data } = await api.get(`/admin/vault/documents/${doc.doc_id}`);
      setDetail(data);
    })();
  }, [doc.doc_id]);
  return (
    <Modal onClose={onClose} testid="vault-history-modal" title={`Decision history — ${doc.doc_id}`}>
      {!detail ? (
        <div className="font-mono text-xs overline">Loading…</div>
      ) : (
        <>
          <div className="font-mono text-xs" style={{ color: "var(--ink-2)" }}>
            Version {detail.version}
            {detail.supersedes_doc_id ? ` · replaces ${detail.supersedes_doc_id}` : ""}
            {detail.superseded_by_doc_id ? ` · replaced by ${detail.superseded_by_doc_id}` : ""}
          </div>
          {(detail.decision_history || []).length === 0 ? (
            <div className="mt-3 font-mono text-xs" style={{ color: "var(--ink-2)" }}>No review decisions yet.</div>
          ) : (
            <div className="mt-3 space-y-2" data-testid="history-entries">
              {detail.decision_history.map((h, i) => (
                <div key={i} className="cd-card p-3 font-mono text-xs" data-testid={`history-entry-${i}`}>
                  <strong>{h.decision}</strong> by {h.reviewer_email} on {h.review_date}
                  <div style={{ color: "var(--ink-2)", marginTop: 3 }}>Notes: {h.notes}</div>
                  <div style={{ color: "var(--ink-2)" }}>Reason: {h.reason}</div>
                  <div style={{ color: "var(--ink-2)" }}>
                    Checks: {Object.entries(h.checks || {}).map(([k, v]) => `${k}=${v ? "✓" : "✗"}`).join(" · ")}
                  </div>
                </div>
              ))}
            </div>
          )}
          {(detail.revocation_state || {}).revoked ? (
            <div className="mt-3 cd-card p-3 font-mono text-xs" style={{ borderLeft: "3px solid #991b1b" }}>
              REVOKED by {detail.revocation_state.revoked_by} at {detail.revocation_state.revoked_at} — {detail.revocation_state.reason}
            </div>
          ) : null}
        </>
      )}
    </Modal>
  );
}

/* ── Access requests panel ───────────────────────────────────────── */
function AccessRequestsPanel({ requests, onChanged }) {
  return (
    <section className="mt-10 cd-card p-5" data-testid="vault-access-panel">
      <div className="overline flex items-center">
        <KeyRound size={12} className="mr-2" />
        <span>BUYER ACCESS REQUESTS ({requests.length})</span>
        <HelpTip text="Raw documents are private. Buyers must request access with a purpose; you approve with a scope and an expiration. Every file access is logged." />
      </div>
      {requests.length === 0 ? (
        <div className="mt-3 font-mono text-xs" style={{ color: "var(--ink-2)" }} data-testid="vault-access-empty">
          No access requests yet.
        </div>
      ) : (
        <div className="mt-3 space-y-2">
          {requests.map((r) => <AccessRow key={r.request_id} r={r} onChanged={onChanged} />)}
        </div>
      )}
    </section>
  );
}

function AccessRow({ r, onChanged }) {
  const [scope, setScope] = useState("");
  const [days, setDays] = useState(14);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const statusColor = { pending: "#92400e", approved: "#166534", denied: "#991b1b", revoked: "#7f1d1d", expired: "#991b1b" }[r.status] || "var(--ink-2)";

  async function act(path, body, msg) {
    setBusy(true);
    try {
      await api.post(`/admin/vault/access-requests/${r.request_id}/${path}`, body);
      onChanged(msg);
    } catch (e) {
      onChanged(`Action failed: ${e.response?.data?.detail || e.message}`);
    } finally { setBusy(false); }
  }

  return (
    <div className="cd-card p-3" data-testid={`access-req-${r.request_id}`}>
      <div className="font-mono text-xs flex flex-wrap items-center gap-2">
        <strong style={{ color: statusColor, textTransform: "uppercase" }} data-testid={`access-status-${r.request_id}`}>{r.status}</strong>
        <span>{r.requester_email}</span>
        <span style={{ color: "var(--ink-2)" }}>→ {r.doc_id} · {r.plant_slug}</span>
      </div>
      <div className="font-mono text-xs mt-1" style={{ color: "var(--ink-2)" }}>Purpose: {r.purpose}</div>
      {r.grant ? (
        <div className="font-mono text-xs mt-1" style={{ color: "var(--ink-2)" }}>
          Grant: scope "{r.grant.scope || "—"}" · expires {(r.grant.expires_at || "").slice(0, 16).replace("T", " ")}
          {r.grant.revoked ? ` · REVOKED (${r.grant.revoke_reason || ""})` : ""}
        </div>
      ) : null}
      {r.status === "pending" ? (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <input value={scope} onChange={(e) => setScope(e.target.value)} placeholder="Grant scope (e.g. view for sourcing audit)" className="cd-input font-mono text-xs" style={{ maxWidth: 300, height: 32 }} data-testid={`access-scope-${r.request_id}`} />
          <input type="number" min={1} max={90} value={days} onChange={(e) => setDays(Number(e.target.value) || 14)} className="cd-input font-mono text-xs" style={{ width: 80, height: 32 }} title="Days until expiry" data-testid={`access-days-${r.request_id}`} />
          <button type="button" disabled={busy} onClick={() => act("approve", { scope, expires_days: days }, "Access approved.")} className="btn btn-primary" style={{ padding: "5px 10px", fontSize: 11, minHeight: 0 }} data-testid={`access-approve-${r.request_id}`}>Approve</button>
          <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Deny reason" className="cd-input font-mono text-xs" style={{ maxWidth: 180, height: 32 }} data-testid={`access-deny-reason-${r.request_id}`} />
          <button type="button" disabled={busy || !reason.trim()} onClick={() => act("deny", { reason }, "Access denied.")} className="btn btn-ghost" style={{ padding: "5px 10px", fontSize: 11, minHeight: 0 }} data-testid={`access-deny-${r.request_id}`}>Deny</button>
        </div>
      ) : null}
      {r.status === "approved" ? (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Revoke reason" className="cd-input font-mono text-xs" style={{ maxWidth: 240, height: 32 }} data-testid={`access-revoke-reason-${r.request_id}`} />
          <button type="button" disabled={busy || !reason.trim()} onClick={() => act("revoke", { reason }, "Access revoked.")} className="btn" style={{ padding: "5px 10px", fontSize: 11, minHeight: 0, border: "1px solid #fca5a5", color: "#991b1b" }} data-testid={`access-revoke-${r.request_id}`}>Revoke grant</button>
        </div>
      ) : null}
    </div>
  );
}

/* ── Shared modal shell ──────────────────────────────────────────── */
function Modal({ children, onClose, title, testid }) {
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 200, overflowY: "auto" }} onClick={onClose} data-testid={`${testid}-overlay`}>
      <div
        className="cd-card"
        style={{ maxWidth: 680, margin: "5vh auto", background: "white", padding: 24, position: "relative" }}
        onClick={(e) => e.stopPropagation()}
        data-testid={testid}
      >
        <button type="button" onClick={onClose} aria-label="Close" style={{ position: "absolute", top: 12, right: 12 }} data-testid={`${testid}-close`}>
          <X size={16} />
        </button>
        <div className="overline flex items-center gap-2">
          <AlertOctagon size={12} /> {title}
        </div>
        <div className="mt-3">{children}</div>
      </div>
    </div>
  );
}
